/**
 * Cliente do Assinador SERPRO, o programa que roda no computador do perito (E164.F2, ADR-0113).
 *
 * O protocolo é o do cliente JS publicado pelo SERPRO: um WebSocket local em
 * `wss://127.0.0.1:65156/signer`, um comando por mensagem com `requestId`, e a resposta com o
 * mesmo `requestId`. Para PDF, `{command: 'sign', type: 'pdf', inputData: <base64>}` devolve
 * `signature` com o PDF assinado em base64; o perito que desiste devolve `actionCanceled`; a
 * recusa do programa traz `error`. O PIN e a chave ficam no programa — nada disso passa por aqui.
 *
 * Sem dependência nova: `WebSocket` do navegador, injetável nos testes (`criarSocket`).
 */

export const ENDERECO_DO_ASSINADOR = 'wss://127.0.0.1:65156/signer';
export const PAGINA_DO_ASSINADOR = 'https://artefatos-assinador.serpro.gov.br/';

export type MotivoDoAssinador = 'ausente' | 'cancelado' | 'recusado' | 'tempo';

export class AssinadorError extends Error {
  public readonly motivo: MotivoDoAssinador;

  constructor(motivo: MotivoDoAssinador, detalhe?: string) {
    super(detalhe ?? motivo);
    this.motivo = motivo;
    this.name = 'AssinadorError';
  }
}

export interface SocketDoAssinador {
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  send(dados: string): void;
  close(): void;
}

export interface OpcoesDoAssinador {
  endereco?: string;
  /** Quanto esperar a conexão abrir (o programa responde na hora quando está no ar). */
  tempoDeConexaoMs?: number;
  /** Quanto esperar a assinatura: o perito escolhe o certificado e digita o PIN. */
  tempoDeAssinaturaMs?: number;
  criarSocket?: (endereco: string) => SocketDoAssinador;
}

export interface Assinador {
  /** Assina o PDF (base64) e devolve o PDF assinado (base64). */
  assinarPdf(pdfBase64: string): Promise<string>;
  fechar(): void;
}

interface Resposta {
  requestId?: unknown;
  actionCanceled?: unknown;
  signature?: unknown;
  error?: unknown;
}

type Pendente = { resolver: (r: Resposta) => void; rejeitar: (e: AssinadorError) => void };

function socketDoNavegador(endereco: string): SocketDoAssinador {
  return new WebSocket(endereco) as unknown as SocketDoAssinador;
}

function lerResposta(dados: unknown): Resposta | null {
  if (typeof dados !== 'string') return null;
  try {
    const lido: unknown = JSON.parse(dados);
    return typeof lido === 'object' && lido !== null ? (lido as Resposta) : null;
  } catch {
    return null;
  }
}

class Conexao implements Assinador {
  private readonly pendentes = new Map<string, Pendente>();
  private proximo = Date.now();

  constructor(
    private readonly socket: SocketDoAssinador,
    private readonly tempoDeAssinaturaMs: number,
  ) {
    socket.onmessage = (ev) => this.receber(ev.data);
    const caiu = () => this.falharTodos(new AssinadorError('ausente'));
    socket.onclose = caiu;
    socket.onerror = caiu;
  }

  private receber(dados: unknown): void {
    const resposta = lerResposta(dados);
    if (resposta === null) return;
    const chave = String(resposta.requestId ?? '');
    const pendente = this.pendentes.get(chave);
    if (pendente === undefined) return;
    this.pendentes.delete(chave);
    pendente.resolver(resposta);
  }

  private falharTodos(erro: AssinadorError): void {
    for (const pendente of this.pendentes.values()) pendente.rejeitar(erro);
    this.pendentes.clear();
  }

  async assinarPdf(pdfBase64: string): Promise<string> {
    this.proximo += 1;
    const requestId = String(this.proximo);
    const resposta = await new Promise<Resposta>((resolver, rejeitar) => {
      const relogio = setTimeout(() => {
        this.pendentes.delete(requestId);
        rejeitar(new AssinadorError('tempo'));
      }, this.tempoDeAssinaturaMs);
      this.pendentes.set(requestId, {
        resolver: (r) => {
          clearTimeout(relogio);
          resolver(r);
        },
        rejeitar: (e) => {
          clearTimeout(relogio);
          rejeitar(e);
        },
      });
      this.socket.send(
        JSON.stringify({ command: 'sign', type: 'pdf', inputData: pdfBase64, requestId }),
      );
    });
    if (resposta.actionCanceled === true) throw new AssinadorError('cancelado');
    if (resposta.error !== undefined && resposta.error !== null) {
      throw new AssinadorError('recusado', String(resposta.error));
    }
    if (typeof resposta.signature !== 'string' || resposta.signature.length === 0) {
      throw new AssinadorError('recusado');
    }
    return resposta.signature;
  }

  fechar(): void {
    this.socket.onclose = null;
    this.socket.onerror = null;
    this.socket.close();
  }
}

/** Abre a conexão com o assinador local; `ausente` quando o programa não está no ar. */
export function conectarAssinador(opcoes: OpcoesDoAssinador = {}): Promise<Assinador> {
  const criar = opcoes.criarSocket ?? socketDoNavegador;
  const tempoDeConexao = opcoes.tempoDeConexaoMs ?? 3000;
  const tempoDeAssinatura = opcoes.tempoDeAssinaturaMs ?? 5 * 60 * 1000;
  return new Promise<Assinador>((resolver, rejeitar) => {
    let socket: SocketDoAssinador;
    try {
      socket = criar(opcoes.endereco ?? ENDERECO_DO_ASSINADOR);
    } catch {
      rejeitar(new AssinadorError('ausente'));
      return;
    }
    const desistir = () => {
      clearTimeout(relogio);
      socket.onopen = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        socket.close();
      } catch {
        // o socket que não abriu pode recusar o close; não há o que limpar
      }
      rejeitar(new AssinadorError('ausente'));
    };
    const relogio = setTimeout(desistir, tempoDeConexao);
    socket.onerror = desistir;
    socket.onclose = desistir;
    socket.onopen = () => {
      clearTimeout(relogio);
      resolver(new Conexao(socket, tempoDeAssinatura));
    };
  });
}

/** O assinador está instalado e no ar? Abre e fecha a conexão. */
export async function assinadorNoAr(opcoes: OpcoesDoAssinador = {}): Promise<boolean> {
  try {
    (await conectarAssinador(opcoes)).fechar();
    return true;
  } catch {
    return false;
  }
}
